/**
 * QuizHub (TASK-014 Step B3 / TASK-015 Phase 2) — Verifica, the quiz
 * sessions hub.
 *
 * Rewires the SAME `TestsHub` component (@arianna/reader-ui/quiz) the old
 * QuizOverlay used, under this page's own URL instead of client-only overlay
 * state (see task doc, Step B3 — no visual redesign beyond the discard
 * confirmation dialog). `quizHub.sessions` is already `TestsHubSession`-
 * shaped (ReaderController#quiz_hub reuses `QuizPresenter.summary`), so it
 * needs no adapter.
 *
 * TASK-015 Phase 2: "Scarta" now really deletes the quiz server-side
 * (`DELETE .../sessions/:id`) instead of only hiding it from this render —
 * the pre-Phase-2 gap this replaces is documented in git history, not here.
 */
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { TestsHub } from '@arianna/reader-ui/quiz'
import { createQuizSession, deleteQuiz } from './quizApi.js'
import { quizSessionUrl } from './urls.js'
import { ConfirmDialog } from './ConfirmDialog.js'
import CourseLayout from './CourseLayout.js'
import type { PageComponent } from './inertiaPage.js'
import type { QuizHubPageProps } from './types.js'

// Matches the old Reader.tsx default (`useState<number>(25)`). The server
// does not currently accept a caller-supplied session length (see
// QuizQuestionSelector::DEFAULT_COUNT doc) — numQ is kept here only because
// TestsHub's UI still renders the pill selector; wiring it through is a
// pre-existing gap, not something this rewire introduces or fixes.
const DEFAULT_NUM_Q = 25

const QuizHub: PageComponent<QuizHubPageProps> = ({ shell, quizHub }) => {
  const { manualVersionId } = shell
  const [numQ, setNumQ] = useState(DEFAULT_NUM_Q)
  const [error, setError] = useState<string | null>(null)
  const [discardTargetId, setDiscardTargetId] = useState<string | null>(null)

  const handleNewSession = () => {
    setError(null)
    void createQuizSession(manualVersionId, 'coverage')
      .then((sessionId) => router.visit(quizSessionUrl(manualVersionId, sessionId)))
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
  }

  const handleDiscardConfirm = () => {
    const id = discardTargetId
    if (id == null) return
    setDiscardTargetId(null)
    setError(null)
    void deleteQuiz(manualVersionId, id)
      .then(() => router.reload())
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {error != null && (
        <div
          style={{
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--red)',
            color: 'var(--surface)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      <TestsHub
        totalQuestions={quizHub.totalQuestions}
        sessions={quizHub.sessions}
        numQ={numQ}
        onNumQChange={setNumQ}
        onNewSession={handleNewSession}
        onResumeSession={(id) => router.visit(quizSessionUrl(manualVersionId, id))}
        // Every completed session now has its own real, server-rendered URL
        // (ReaderController#quiz_session results phase) — unlike the old
        // overlay, "Rivedi" works for ANY historical session, not just the
        // one just played in this browser tab.
        onViewResults={(id) => router.visit(quizSessionUrl(manualVersionId, id))}
        onDiscardSession={(id) => setDiscardTargetId(id)}
      />
      {discardTargetId != null && (
        <ConfirmDialog
          titleId="quiz-discard-confirm-title"
          title="Scartare questo quiz?"
          message="Il quiz e le sue risposte andranno persi. Questa azione non può essere annullata."
          confirmLabel="Scarta"
          cancelLabel="Annulla"
          destructive
          onConfirm={handleDiscardConfirm}
          onCancel={() => setDiscardTargetId(null)}
        />
      )}
    </div>
  )
}

QuizHub.layout = (page) => <CourseLayout>{page}</CourseLayout>

export default QuizHub
